import Highcharts from 'highcharts';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import { useTheme } from '../../../../../../theme/theme';
import { ChartDataDTO, PlanSeriesName } from '../../../../../api/useDecumulationGoalQuery';

interface Params {
  data: ChartDataDTO;
}

export const useDecumulationChart = ({ data }: Params) => {
  const theme = useTheme();

  const { Categories, Series } = data;

  const seriesStackOrder = [PlanSeriesName.Confidence50, PlanSeriesName.Confidence90];

  const chartSeries = seriesStackOrder.map(seriesName => ({
    name: seriesName,
    color: '',
    data: Series[seriesName].map((series, index) => ({
      x: parseInt(Categories[index], 10),
      y: series,
    })),
  }));

  const chartConfig: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    title: undefined,
    chart: {
      height: 430,
      spacingRight: 20,
      spacingBottom: 20,
      spacingTop: 50,
      style: { fontFamily: 'Inter', fontSize: '16px' },
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 1,
      },
      series: {
        marker: { enabled: false, symbol: 'circle' },
        events: {
          legendItemClick: event => {
            event.preventDefault();
          },
        },
      },
    },
    colors: seriesStackOrder.map(seriesName =>
      seriesName === PlanSeriesName.Confidence50
        ? theme.colors.primary['300']
        : theme.colors.primary['100'],
    ),
    legend: {
      enabled: false,
    },
    tooltip: {
      borderRadius: 10,
      shadow: false,
      useHTML: true,
      backgroundColor: theme.colors.neutral['700'],
      borderColor: theme.colors.neutral['700'],
      shape: 'square',
      style: {
        color: theme.colors.neutral['100'],
      },
      formatter() {
        return `
            <div style='display: flex; align-items: flex-start;'>
              <div style='width: 8px; height: 8px; border-radius: 4px; margin-top: 5px; margin-right: 4px; background: ${
                this.series.name === PlanSeriesName.Confidence50
                  ? `${theme.colors.primary['300']}`
                  : `${theme.colors.primary['100']}`
              }'></div>
              
              <div style='font-size: 12px; color: ${theme.colors.neutral['100']};'>
                <div style='margin-bottom: 4px;'>
                  <span>${
                    this.series.name === PlanSeriesName.Confidence50
                      ? '50% Confidence:'
                      : '90% Confidence:'
                  }</span>
                  <span style='font-weight: 600; margin-left: 4px;'>${this.x}</span>
                </div>
                <div style="display: flex; justify-content: flex-end">
                  <span style='font-weight: 600'>
                    ${formatCurrency(Number(this.y), {
                      average: true,
                      mantissa: 2,
                    }).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          `;
      },
    },
    xAxis: {
      tickWidth: 0,
      allowDecimals: false,
      labels: {
        style: {
          color: theme.colors.neutral['500'],
        },
      },
    },
    yAxis: {
      title: {
        text: null,
      },
      max: null,
      labels: {
        enabled: true,
        formatter() {
          return formatCurrency(Number(this.value), {
            average: true,
            mantissa: 2,
            optionalMantissa: true,
            trimMantissa: true,
          }).toUpperCase();
        },
        style: {
          color: theme.colors.neutral['500'],
        },
      },
      gridLineDashStyle: 'Solid',
      gridLineColor: theme.colors.neutral['200'],
    },
    series: chartSeries.map(_series => ({
      ..._series,
      type: 'areaspline',
      lineWidth: 2,
      lineColor:
        _series.name === PlanSeriesName.Confidence90
          ? theme.colors.primary['500']
          : theme.colors.primary['300'],
      marker: {
        fillColor:
          _series.name === PlanSeriesName.Confidence90
            ? theme.colors.primary['500']
            : theme.colors.primary['300'],
      },
    })),
  };

  return {
    chartConfig,
  };
};
